import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { createPortal } from 'react-dom';

import { ComponentTypes, EditorMode, StoryMediaPlatform } from 'types/story';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { COMPONENT_TYPE } from 'common/constants';
import { EventEmitter } from 'utils/event-emitter';
import { ClientReducerState } from 'client/reducers';
import { setEditorSelectedNode } from 'client/actions';
import { PAGE_SCROLL_LOCK_ATTR } from 'client/constants/common';
import { canChangeHeight, canChangeWidth } from 'common/utils/blocks/features';
import { SelectionHintController } from 'client/components/common/SelectionHint/SelectionHintController';
import {
	Listener as SelectionHintEventListener,
	SelectedHintEventDetail,
	SelectionHintEvent,
} from 'client/components/common/SelectionHint/SelectionHintEvent';
import { Context as ResponsiveProviderContext } from 'client/components/common/ResponsiveProvider/Context';
import { ResponsiveProviderContextType } from 'client/components/common/ResponsiveProvider/types';
import { BOX_TYPE } from 'client/components/common/BuildingBlocks/Box';
import { TimerType } from 'client/components/common/BuildingBlocks/Timer/Timer';
import {
	getSelectionGroupPosition,
	getSelectionPosition,
	SELECTION_STAGE,
	SELECTION_TYPES,
} from 'client/components/common/SelectionHint/utils';
import { FIELD_EDITABLE_ATTR } from 'src/routes/client/components/common/BuildingBlocks/Fields/constants';
import { AnswerProgressType } from 'client/components/common/BuildingBlocks/AnswerProgress/AnswerProgress';
import Points from './Draggable/Points';
import { SIDE, SideType, SUPPORTED_PROP } from './Draggable/Points/constants';
import Breadcrumbs from './Breadcrumbs';
import Toolbar from './Toolbar';
import css from './SelectionHint.scss';
import { SelectionSingleType } from './types';

const isChildOf = (selection: SelectionSingleType, parentType: (typeof BOX_TYPE)[keyof typeof BOX_TYPE]) => {
	const parent = selection.editableModeProps.inheritance?.parent;
	if (!parent) return false;
	const lastParent = _.last(parent);
	const boxType = lastParent?.inheritance.componentProps?.other?.boxType;
	if (!boxType) return false;
	return _.last(boxType)!.value === parentType;
};

// set of points that shouldn't be rendered
const getHiddenPoints = ({ selection, isStackChild }: { selection: SelectionSingleType; isStackChild?: boolean }) => {
	const hiddenPoints = new Set<SideType>([
		...(canChangeHeight(selection.type, selection.uiConfig)
			? []
			: [SIDE.leftTop, SIDE.leftBottom, SIDE.rightTop, SIDE.rightBottom, SIDE.top, SIDE.bottom]),
		...(canChangeWidth(selection.type, selection.uiConfig)
			? []
			: [SIDE.leftTop, SIDE.leftBottom, SIDE.rightTop, SIDE.rightBottom, SIDE.left, SIDE.right]),
	]);

	const cornerSides = [SIDE.leftTop, SIDE.leftBottom, SIDE.rightTop, SIDE.rightBottom];
	const enableCorners = () => cornerSides.forEach(side => hiddenPoints.delete(side));
	const disableCorners = () => cornerSides.forEach(side => hiddenPoints.add(side));

	if (selection.type === COMPONENT_TYPE.TIMER) {
		const timerType = selection.uiConfig.componentProps?.timerType;
		if (timerType === TimerType.circ) enableCorners();
		else if (timerType === TimerType.bar) disableCorners();
	} else if (selection.type === COMPONENT_TYPE.ANSWER_PROGRESS) {
		if (selection.uiConfig.componentProps?.answerProgressType === AnswerProgressType.bar) disableCorners();
	}

	// must
	if (isStackChild) {
		disableCorners();
		hiddenPoints.add(SIDE.left);
		hiddenPoints.add(SIDE.right);
	}

	return hiddenPoints;
};

type IsAllowToSelect = {
	selection: ClientReducerState['editor']['selection'];
	selectionType: SelectedHintEventDetail['selectionType'];
	componentProps: SelectedHintEventDetail['componentProps'];
	event: SelectedHintEventDetail['originalEvent'];
};

function isAllowToSelect({ selection, selectionType, componentProps, event }: IsAllowToSelect) {
	if (selectionType !== SELECTION_TYPES.clicked) {
		return true;
	}

	const clickedSelection = selection[SELECTION_TYPES.clicked];
	const isGlobal = isLayerType(componentProps).global;
	const isMultiSelect = event.shiftKey;
	const isAllowGlobal =
		_.size(clickedSelection) && isMultiSelect
			? _.values(clickedSelection).some(el => isLayerType(el).global)
			: isGlobal;

	return isAllowGlobal === isGlobal;
}

const isSelection = {
	typeOf: (selection: SelectionSingleType, type: ComponentTypes) => selection.type === type,
	childOfType: (selection: SelectionSingleType, type: ComponentTypes) =>
		selection.editableModeProps.inheritance?.parent.some(parent => parent.type === type),
};

type SelectionHintForceUpdateEvent = { type: 'forceUpdate' };

export const SelectionHintEvEm = new EventEmitter<SelectionHintForceUpdateEvent>();

type State = {
	dragProp: string; //    dragProp property name (eg. size, position...)
	dragId: string; //      is of the draggable block
	isScrolling: boolean;
};

type Props = ConnectedProps<typeof connector>;

class SelectionHint extends React.Component<Props, State> {
	state = {
		dragProp: '',
		dragId: '',
		isScrolling: false,
	};

	scrollTimeout?: number;

	SelectionHintController?: SelectionHintController;

	SelectionHintEvent?: SelectionHintEvent;

	componentDidMount() {
		document.addEventListener('scroll', this.onScroll);
		window.addEventListener('resize', this.onScroll);
		SelectionHintEvEm.addListener('forceUpdate', this.forceUpdateListener);

		this.SelectionHintController = new SelectionHintController({
			action: this.props.setEditorSelectedNode,
		});

		this.SelectionHintEvent = new SelectionHintEvent({
			listener: this.selectionHintEventListener,
		});

		this.SelectionHintEvent.addListener();
	}

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		// shouldn't update while dragging
		if (Boolean(this.state.dragProp) && Boolean(nextState.dragProp)) return false;
		if (Boolean(this.state.dragProp) && !nextState.dragProp) return true;
		return true;
	}

	componentWillUnmount() {
		document.removeEventListener('scroll', this.onScroll);
		window.removeEventListener('resize', this.onScroll);
		SelectionHintEvEm.removeListener('forceUpdate', this.forceUpdateListener);
		this.SelectionHintEvent!.removeListener();
	}

	forceUpdateListener = () => {
		this.forceUpdate();
	};

	selectionHintEventListener: SelectionHintEventListener = e => {
		if (!this.SelectionHintController) {
			console.error('this.SelectionHintController does not exists');
			return;
		}
		const { selectionType, componentProps, originalEvent, trigger, stage } = e.detail;

		switch (trigger) {
			case SelectionHintController.FOCUS:
				if (componentProps) this.SelectionHintController.focus(originalEvent, selectionType, componentProps);
				break;
			case SelectionHintController.SELECT:
				if (
					componentProps &&
					isAllowToSelect({
						selection: this.props.selection,
						componentProps,
						selectionType,
						event: originalEvent,
					})
				) {
					this.SelectionHintController.select(
						originalEvent,
						selectionType,
						componentProps,
						stage,
						this.props.editorMode
					);
				}
				break;
			case SelectionHintController.DESELECT:
				this.SelectionHintController.deselect(originalEvent, selectionType);
				break;
			default:
				break;
		}
	};

	onScroll = event => {
		const pageScrollLock = document.body.getAttribute(PAGE_SCROLL_LOCK_ATTR);
		if (pageScrollLock !== null) {
			const [x, y] = pageScrollLock.split(',');
			document.documentElement.scroll(+x, +y);
			return;
		}

		if (!this.scrollTimeout) {
			this.setState({ isScrolling: true });
		}
		window.clearTimeout(this.scrollTimeout);
		this.scrollTimeout = window.setTimeout(this.onScrollEnd, 100);
	};

	onScrollEnd = () => {
		delete this.scrollTimeout;
		this.setState({ isScrolling: false });
	};

	/**
	 * @param dragProp {String}
	 * @param dragId {String}
	 */
	setDragging = (dragProp: State['dragProp'], dragId: State['dragId']) => {
		if (dragId) document.body.setAttribute('data-is-drag', '');
		else document.body.removeAttribute('data-is-drag');

		this.setState({ dragProp, dragId });
	};

	/**
	 * Render draggable points to update selected element style on drag
	 * @param type {String}
	 * @param selection {Object} selection data object or object with a pair key value for each selection
	 * @param selectionStyle {Object}
	 * @param currentMediaQuery {String}
	 * @param selectionGroup {Object|null}
	 * @param isMultiple {Boolean}
	 * @param boundaries {Object} media query boundary values
	 * @return {null|*}
	 */
	renderPoints = ({
		type,
		selection,
		selectionStyle,
		currentMediaQuery,
		selectionGroup,
		isMultiple,
		boundaries,
	}: {
		type: keyof Props['selection'];
		selection: SelectionSingleType;
		selectionStyle: Record<string, string | number>;
		currentMediaQuery: StoryMediaPlatform;
		selectionGroup: Props['selection']['clicked'];
		isMultiple: boolean;
		boundaries: ResponsiveProviderContextType['boundaries'];
	}) => {
		if (
			this.props.editorMode === EditorMode.CONTENT ||
			type === SELECTION_TYPES.hovered ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CONTENT) ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CARD)
		) {
			return null;
		}

		const isHiddenPositionPoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.position;
		const isHiddenSizePoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.size;

		const isStackChild = isChildOf(selection, BOX_TYPE.STACK);
		const hiddenPoints = getHiddenPoints({ selection, isStackChild });
		// forbid to move by X axis
		const lockX = _.get(selection, 'uiConfig.componentProps.styles.justifySelf') === 'stretch' || isStackChild;
		// forbid to move by Y axis
		const lockY = _.get(selection, 'uiConfig.componentProps.styles.alignSelf') === 'stretch';

		if (isChildOf(selection, BOX_TYPE.GRID)) {
			return null;
		}

		return (
			<React.Fragment key={selection.id}>
				<Points
					hidden={isHiddenPositionPoints}
					name={SUPPORTED_PROP.position}
					style={selectionStyle as any}
					selection={selection}
					selectionGroup={selectionGroup}
					isMultiple={isMultiple}
					setDragging={this.setDragging}
					currentState={this.props.currentState}
					currentMediaQuery={currentMediaQuery}
					lockX={lockX}
					lockY={lockY}
					boundaries={boundaries}
				/>
				{!isMultiple && selection.type !== COMPONENT_TYPE.GOOGLE_LOGIN && (
					<Points
						hidden={isHiddenSizePoints}
						name={SUPPORTED_PROP.size}
						style={selectionStyle as any}
						selection={selection}
						selectionGroup={selectionGroup}
						isMultiple={isMultiple}
						setDragging={this.setDragging}
						currentState={this.props.currentState}
						currentMediaQuery={currentMediaQuery}
						lockX={lockX}
						lockY={lockY}
						hiddenPoints={hiddenPoints}
						boundaries={boundaries}
					/>
				)}
			</React.Fragment>
		);
	};

	renderSelection = ({
		type,
		selectionStyle,
		selection,
		currentMediaQuery,
		position,
		selectionGroup,
		isMultiple,
		boundaries,
		isEditing,
	}: {
		type: keyof Props['selection'];
		selectionStyle: Record<string, string | number>;
		selection: SelectionSingleType;
		currentMediaQuery: StoryMediaPlatform;
		position: CSSStyleDeclaration['position'];
		selectionGroup: Props['selection']['clicked'];
		isMultiple: boolean;
		boundaries: ResponsiveProviderContextType['boundaries'];
		isEditing: boolean;
	}) => {
		const isRenderToolbar = !isMultiple && !this.state.dragProp;
		const isHiddenSelection = this.state.dragProp === SUPPORTED_PROP.position && this.state.dragId !== selection.id;
		const isHiddenSelectionRect = Boolean(this.state.dragProp);

		return (
			<div
				className={css.selectionW}
				data-selection-position={position}
				data-selection-type={type}
				key={`selection-${type}-${selection.id}`}
				hidden={isHiddenSelection}
			>
				<div
					className={cn(css.selection, `selection-${type}`, {
						[css.selectionAccent]:
							isEditing ||
							(type === SELECTION_TYPES.clicked && selection.selectionStage === SELECTION_STAGE.INNER),
					})}
					style={selectionStyle}
					hidden={isHiddenSelectionRect}
				>
					{isRenderToolbar && (
						<Toolbar>
							<Breadcrumbs selection={selection} />
						</Toolbar>
					)}
				</div>
				{this.renderPoints({
					type,
					selection,
					selectionStyle,
					currentMediaQuery,
					isMultiple,
					selectionGroup,
					boundaries,
				})}
			</div>
		);
	};

	renderSelections = ({
		currentMediaQuery,
		boundaries,
	}: {
		currentMediaQuery: StoryMediaPlatform;
		boundaries: ResponsiveProviderContextType['boundaries'];
	}) => {
		const clickedSelections = this.props.selection[SELECTION_TYPES.clicked];
		const isGroupSelected = _.size(clickedSelections) > 1;

		return (Object.keys(this.props.selection) as (keyof Props['selection'])[]).map(type => {
			const values = this.props.selection[type];
			const [id, selection] = Object.entries(values)[0] || [];

			const node = document.getElementById(id);
			const isSkipRender =
				!node ||
				this.state.isScrolling ||
				// is hovered node already clicked
				(type === SELECTION_TYPES.hovered && this.props.selection[SELECTION_TYPES.clicked][id]);

			if (isSkipRender) {
				return null;
			}

			const isGroup = type === SELECTION_TYPES.clicked && isGroupSelected;
			const selectionStyle = isGroup ? getSelectionGroupPosition(clickedSelections) : getSelectionPosition(node);
			let position: CSSStyleDeclaration['position'] = 'relative';

			if (isGroup) {
				position = selectionStyle.position;
			} else if (node) {
				position = window.getComputedStyle(node).position;
			}

			const isEditing =
				type === SELECTION_TYPES.clicked &&
				(node.contentEditable === 'true' ||
					node.hasAttribute(FIELD_EDITABLE_ATTR) ||
					node.querySelector('[contenteditable="true"]') !== null ||
					node.querySelector(`[${FIELD_EDITABLE_ATTR}]`) !== null);

			return this.renderSelection({
				currentMediaQuery,
				type,
				selection,
				selectionGroup: clickedSelections,
				selectionStyle,
				position,
				isMultiple: isGroup,
				boundaries,
				isEditing,
			});
		});
	};

	render() {
		/* Manual content resize: <ContentResize> is removed in commit - d00044079d658fd29d9f8c76474f43c11ab34cfd */
		return (
			<ResponsiveProviderContext.Consumer>
				{({ currentMediaQuery, boundaries }) =>
					currentMediaQuery &&
					boundaries &&
					createPortal(this.renderSelections({ currentMediaQuery, boundaries }), document.body)
				}
			</ResponsiveProviderContext.Consumer>
		);
	}
}

const mapStateToProps = (state: ClientReducerState) => ({
	selection: state.editor.selection,
	currentState: state.editor.state.state,
	editorMode: state.editor.editorMode,
});
const mapDispatchToProps = {
	setEditorSelectedNode,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(SelectionHint);
